import { useEffect, useRef, useState } from "react";
import { QueryFunction, useQuery, useQueryClient } from "@tanstack/react-query";
import { cloneDeep } from "lodash";
import { socketConnection } from "@/lib/natsConnection.ts";

type UpdateQueryDataFunction<TData, TSocketMessage = TData> = (
  currentData: TData,
  socketMessage: TSocketMessage,
) => TData;

interface UseThrottledSocketOptions<TData> {
  apiUrl?: string;
  requestHeaders?: Record<string, string>;
  fetchInitialData?: QueryFunction<TData>;
  selector?: (state: any) => TData;
  processInitialDataFunction?: (apiResponse: any) => TData;
  throttleInterval?: number;
}

export function useThrottledSocket<TData, TSocketMessage = TData>(
  topic: string,
  updateQueryData: UpdateQueryDataFunction<TData, TSocketMessage> = (
    _,
    newData,
  ) => newData as unknown as TData,
  options?: UseThrottledSocketOptions<TData>,
) {
  const queryClient = useQueryClient();
  const unsubscribeRef = useRef<() => void>();
  const lastUpdateRef = useRef<number>(0);
  const pendingUpdateRef = useRef<TSocketMessage | null>(null);
  const throttleInterval = options?.throttleInterval || 1000; // Default to 1 second

  useEffect(() => {
    let isMounted = true;
    let unsubscribe: (() => void) | undefined;
    let throttleTimeout: ReturnType<typeof setTimeout> | undefined;

    const processUpdate = (newData: TSocketMessage) => {
      const now = Date.now();
      if (now - lastUpdateRef.current >= throttleInterval) {
        // If enough time has passed, update immediately
        queryClient.setQueryData([topic], (currentData: TData) =>
          updateQueryData(cloneDeep(currentData), newData),
        );
        lastUpdateRef.current = now;
        pendingUpdateRef.current = null;
      } else {
        // Store the update to be processed later
        pendingUpdateRef.current = newData;

        // Clear existing timeout if any
        if (throttleTimeout) {
          clearTimeout(throttleTimeout);
        }

        // Schedule the next update
        throttleTimeout = setTimeout(
          () => {
            if (pendingUpdateRef.current && isMounted) {
              queryClient.setQueryData([topic], (currentData: TData) =>
                updateQueryData(
                  cloneDeep(currentData),
                  pendingUpdateRef.current!,
                ),
              );
              lastUpdateRef.current = Date.now();
              pendingUpdateRef.current = null;
            }
          },
          throttleInterval - (now - lastUpdateRef.current),
        );
      }
    };

    const subscribeToTopic = async () => {
      if (isMounted && !unsubscribe) {
        unsubscribe = await socketConnection.subscribe(topic, processUpdate);
        if (isMounted) {
          unsubscribeRef.current = unsubscribe;
        } else {
          unsubscribe?.();
        }
      }
    };

    subscribeToTopic().catch(console.error);

    return () => {
      isMounted = false;
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
      unsubscribeRef.current?.();
      unsubscribe?.();
    };
  }, [topic, updateQueryData, throttleInterval, queryClient]);

  const {
    apiUrl,
    requestHeaders,
    fetchInitialData,
    selector,
    processInitialDataFunction,
  } = options || {};

  const query = useQuery<TData>({
    queryKey: [topic],
    queryFn:
      fetchInitialData ||
      (async () => {
        if (!apiUrl) {
          return {} as TData;
        }
        const response = await fetch(apiUrl, {
          headers: requestHeaders || {},
        });
        const data = await response.json();
        return processInitialDataFunction
          ? processInitialDataFunction(data)
          : selector
            ? selector(data)
            : data;
      }),
  });

  return query;
}
